import type { PermissionEnum } from '@Application/Security/Roles';
import type { RoleCreationFailedAction } from '@Application/Security/Roles/Actions/roleCreationFailed';
import type { RoleUpdateFailedAction } from '@Application/Security/Roles/Actions/roleUpdateFailed';
import CreateRole from '@Application/Security/Roles/Commands/CreateRole';
import UpdateRole from '@Application/Security/Roles/Commands/UpdateRole';
import createRole from '@Application/Security/Roles/createRole';
import updateRole from '@Application/Security/Roles/updateRole';
import { selectRole } from '@UI/Pages/Administration/Roles/Signals/selectedRoleIdSignal';
import roleSidebarSignal, {
	closeRoleSidebar,
} from '@UI/Pages/Administration/Roles/Signals/roleSidebarSignal';
import RoleFormData from '@UI/Pages/Administration/Roles/Sidebars/RoleSidebar/FormData/RoleFormData';
import useStrictAuthenticatedUser from '@UI/Providers/AuthenticatedUserProvider/useStrictAuthenticatedUser';
import * as React from 'react';
import { updateValidationFromExternalSource } from '@verclam/form-validator';
import { useSignalValue } from '@verclam/pulsar';
import { useDispatch, useSelector } from '@verclam/synapse';
import { Button, Form, InputText, Sidebar, TextArea } from '@verclam/ui-kit';

/**
 * One panel for creation, rename and duplication, told apart by `context.type` on the signal. Only
 * carries `label`/`description`: a role's permissions are composed afterwards on the card for the
 * `edit` mode (a rename always preserves the role's current permission set unchanged in the `PUT` it
 * issues, since the endpoint replaces the whole triplet, never patches), and copied once from the
 * source role for `duplicate` — both `edit` and `duplicate` read `permissions` from the already-loaded
 * `rolesStore`, never from a dedicated fetch.
 */
const RoleSidebar: React.FC = () => {
	const dispatch = useDispatch();
	const context = useSignalValue(roleSidebarSignal);
	const roles = useSelector((state) => state.rolesStore.rolesList);
	const { user } = useStrictAuthenticatedUser();

	const [formData, setFormData] = React.useState(() => RoleFormData.createEmpty());
	const [isSubmitting, setIsSubmitting] = React.useState(false);

	const formId = React.useId();
	const roleId = context?.type === 'edit' ? context.roleId : null;
	const sourceRoleId = context?.type === 'duplicate' ? context.sourceRoleId : null;
	const editedRole = roleId !== null ? (roles?.findById(roleId) ?? null) : null;
	const sourceRole = sourceRoleId !== null ? (roles?.findById(sourceRoleId) ?? null) : null;

	// Each of the three modes fills the form its own way: `create` always starts empty, `edit` loads the
	// role it targets, `duplicate` loads the source role and prefixes its label — falling through to
	// `createEmpty()` here would silently wipe the prefill the moment the source role resolves to `null`.
	React.useEffect(() => {
		if (context === null) {
			return;
		}

		if (context.type === 'edit') {
			setFormData(editedRole !== null ? RoleFormData.fromView(editedRole) : RoleFormData.createEmpty());
			return;
		}

		if (context.type === 'duplicate') {
			setFormData(
				sourceRole !== null
					? RoleFormData.fromView(sourceRole).setLabel(`Copie de ${sourceRole.label}`)
					: RoleFormData.createEmpty(),
			);
			return;
		}

		setFormData(RoleFormData.createEmpty());
	}, [context, editedRole, sourceRole]);

	const handleLabelChange = React.useCallback((value: string) => {
		setFormData((current) => current.setLabel(value));
	}, []);

	const handleDescriptionChange = React.useCallback((value: string) => {
		setFormData((current) => current.setDescription(value));
	}, []);

	const handleSubmit = React.useCallback(() => {
		if (!formData.isFormDataValid()) {
			setFormData(formData.showErrors());
			return;
		}

		// Unreachable in practice: `AppShell` never mounts this page without an account.
		if (user.account === null) {
			return;
		}

		const accountId = user.account.id;
		const description = formData.description === '' ? null : formData.description;

		setIsSubmitting(true);

		if (context?.type === 'edit') {
			dispatch(
				updateRole(
					new UpdateRole(
						accountId,
						context.roleId,
						formData.label,
						description,
						(editedRole?.permissions ?? []) as readonly PermissionEnum[],
					),
				),
			)
				.then(() => closeRoleSidebar())
				.catch((action: RoleUpdateFailedAction) =>
					updateValidationFromExternalSource({
						action,
						eventNames: 'ROLE_UPDATE_FAILED',
						formDataUpdater: setFormData,
					}),
				)
				.finally(() => setIsSubmitting(false));
			return;
		}

		// `create` starts from no permissions; `duplicate` is a creation too — it just seeds the request
		// with the source role's current permissions, in the same single `POST /roles` call.
		const permissions =
			context?.type === 'duplicate'
				? ((sourceRole?.permissions ?? []) as readonly PermissionEnum[])
				: [];

		dispatch(createRole(new CreateRole(accountId, formData.label, description, permissions)))
			.then((action) => {
				closeRoleSidebar();

				if (action.type === 'ROLE_CREATED') {
					selectRole(action.payload.id);
				}
			})
			.catch((action: RoleCreationFailedAction) =>
				updateValidationFromExternalSource({
					action,
					eventNames: 'ROLE_CREATION_FAILED',
					formDataUpdater: setFormData,
				}),
			)
			.finally(() => setIsSubmitting(false));
	}, [formData, context, editedRole, sourceRole, user, dispatch]);

	const header =
		context?.type === 'edit'
			? 'Modifier le rôle'
			: context?.type === 'duplicate'
				? 'Dupliquer le rôle'
				: 'Nouveau rôle';

	const formLabel =
		context?.type === 'edit'
			? 'Modifier un rôle'
			: context?.type === 'duplicate'
				? 'Dupliquer un rôle'
				: 'Créer un rôle';

	return (
		<Sidebar
			visible={context !== null}
			onHide={closeRoleSidebar}
			position="right"
			header={header}
			footer={
				<React.Fragment>
					<Button
						type="button"
						severity="secondary"
						outlined
						label="Annuler"
						onClick={closeRoleSidebar}
					/>
					<Button type="submit" form={formId} label="Enregistrer" loading={isSubmitting} />
				</React.Fragment>
			}
			blockScroll
		>
			<Form id={formId} onSubmit={handleSubmit} aria-label={formLabel}>
				<InputText
					name="label"
					label="Libellé"
					required
					value={formData.label}
					onChange={handleLabelChange}
					errorMessage={formData.getValidationMessage('label')}
					isFormFieldValid={formData.isValid('label')}
				/>
				<TextArea
					name="description"
					label="Description"
					value={formData.description}
					onChange={handleDescriptionChange}
					errorMessage={formData.getValidationMessage('description')}
					isFormFieldValid={formData.isValid('description')}
				/>
			</Form>
		</Sidebar>
	);
};

export default RoleSidebar;
